import { useEffect, useState } from 'react';
import { StyleSheet } from 'react-native';
import { FormInput, Text, VStack } from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { SaveProfileInput } from '../types';

type BusinessProfileFields = Omit<SaveProfileInput, 'organizationId' | 'website' | 'languages'>;

const REQUIRED_FIELDS: Array<keyof BusinessProfileFields> = [
  'organizationName',
  'targetMarketLocation',
  'industry',
  'productSummary',
  'targetAudience',
];

const EMPTY_FIELDS: BusinessProfileFields = {
  organizationName: '',
  targetMarketLocation: '',
  industry: '',
  productSummary: '',
  targetAudience: '',
  salesMotion: '',
  tone: '',
};

interface BusinessProfileStepProps {
  onNext: (profile: BusinessProfileFields) => void;
  isSaving: boolean;
  initialValues?: Partial<BusinessProfileFields>;
}

export function BusinessProfileStep({
  onNext,
  isSaving,
  initialValues,
}: Readonly<BusinessProfileStepProps>) {
  const { t } = useTranslation();
  const [values, setValues] = useState<BusinessProfileFields>({
    ...EMPTY_FIELDS,
    ...initialValues,
  });
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (isDirty || !initialValues) return;
    setValues({ ...EMPTY_FIELDS, ...initialValues });
  }, [initialValues, isDirty]);

  const isValid = REQUIRED_FIELDS.every((field) => (values[field] ?? '').trim().length > 0);

  const setField = (field: keyof BusinessProfileFields) => (text: string) => {
    setIsDirty(true);
    setValues((prev) => ({ ...prev, [field]: text }));
  };

  const handlePress = () => {
    if (!isValid) return;
    onNext(values);
  };

  return (
    <VStack spacing="md" testID="onboarding-profile-step">
      <Text variant="h4" color={colors.text.primary}>
        {t('onboardingWizard.profile.title')}
      </Text>
      <Text variant="body" color={colors.text.secondary}>
        {t('onboardingWizard.profile.subtitle')}
      </Text>

      <VStack spacing="xs">
        <Text variant="caption" color={colors.text.secondary} style={styles.label}>
          {t('onboardingWizard.profile.organizationNameLabel')}
        </Text>
        <FormInput
          testID="onboarding-profile-organizationName"
          value={values.organizationName}
          onChangeText={setField('organizationName')}
          placeholder={t('onboardingWizard.profile.organizationNamePlaceholder')}
          style={styles.input}
        />
      </VStack>

      <VStack spacing="xs">
        <Text variant="caption" color={colors.text.secondary} style={styles.label}>
          {t('onboardingWizard.profile.targetMarketLocationLabel')}
        </Text>
        <FormInput
          testID="onboarding-profile-targetMarketLocation"
          value={values.targetMarketLocation}
          onChangeText={setField('targetMarketLocation')}
          placeholder={t('onboardingWizard.profile.targetMarketLocationPlaceholder')}
          style={styles.input}
        />
      </VStack>

      <VStack spacing="xs">
        <Text variant="caption" color={colors.text.secondary} style={styles.label}>
          {t('onboardingWizard.profile.industryLabel')}
        </Text>
        <FormInput
          testID="onboarding-profile-industry"
          value={values.industry}
          onChangeText={setField('industry')}
          placeholder={t('onboardingWizard.profile.industryPlaceholder')}
          style={styles.input}
        />
      </VStack>

      <VStack spacing="xs">
        <Text variant="caption" color={colors.text.secondary} style={styles.label}>
          {t('onboardingWizard.profile.productSummaryLabel')}
        </Text>
        <FormInput
          testID="onboarding-profile-productSummary"
          value={values.productSummary}
          onChangeText={setField('productSummary')}
          placeholder={t('onboardingWizard.profile.productSummaryPlaceholder')}
          style={styles.input}
          multiline
        />
      </VStack>

      <VStack spacing="xs">
        <Text variant="caption" color={colors.text.secondary} style={styles.label}>
          {t('onboardingWizard.profile.targetAudienceLabel')}
        </Text>
        <FormInput
          testID="onboarding-profile-targetAudience"
          value={values.targetAudience}
          onChangeText={setField('targetAudience')}
          placeholder={t('onboardingWizard.profile.targetAudiencePlaceholder')}
          style={styles.input}
        />
      </VStack>

      <VStack spacing="xs">
        <Text variant="caption" color={colors.text.secondary} style={styles.label}>
          {t('onboardingWizard.profile.salesMotionLabel')}
        </Text>
        <FormInput
          testID="onboarding-profile-salesMotion"
          value={values.salesMotion}
          onChangeText={setField('salesMotion')}
          placeholder={t('onboardingWizard.profile.salesMotionPlaceholder')}
          style={styles.input}
        />
      </VStack>

      <VStack spacing="xs">
        <Text variant="caption" color={colors.text.secondary} style={styles.label}>
          {t('onboardingWizard.profile.toneLabel')}
        </Text>
        <FormInput
          testID="onboarding-profile-tone"
          value={values.tone}
          onChangeText={setField('tone')}
          placeholder={t('onboardingWizard.profile.tonePlaceholder')}
          style={styles.input}
        />
      </VStack>

      <Button
        testID="onboarding-profile-next"
        variant="primary"
        onPress={handlePress}
        disabled={!isValid || isSaving}
        loading={isSaving}
        accessibilityState={{ disabled: !isValid || isSaving }}
      >
        {t('onboardingWizard.profile.next')}
      </Button>
    </VStack>
  );
}

const styles = StyleSheet.create({
  label: {
    fontWeight: '600',
  },
  input: {
    borderWidth: 1,
    borderColor: colors.border.subtle,
    backgroundColor: colors.background.secondary,
    borderRadius: tokens.radius.md,
    padding: tokens.spacing.md,
  },
});
